import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import {
  deriveThirdPartyId,
  deriveThirdPartyName,
  findThirdPartyNodeBySoc,
} from "layouts/agent/utils";

const CRM_HEADERS = {
  "Mdm-Auth": "57b2a9ba-4428-48e4-b137-f8337b68595e",
  "Content-Type": "application/json",
};

const CONTACT_LIST_URL =
  "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/contact/list?sortfield=t.rowid&sortorder=ASC&limit=500";
const THIRD_PARTY_LIST_URL =
  "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/mdm/thirdpartylist";

const normalizeEmail = (email) => (typeof email === "string" ? email.trim().toLowerCase() : "");
const normalizeAlias = (alias) =>
  alias === undefined || alias === null ? "" : String(alias).trim();

const resolveThirdPartySocId = (node) => {
  if (!node) return "";
  const candidate =
    node.soc_id ?? node.socid ?? node.rowid ?? node.id ?? node.fk_soc ?? node.company_id ?? "";
  return candidate !== undefined && candidate !== null ? String(candidate).trim() : "";
};

const extractLinkedThirdPartyId = (contact) => {
  if (!contact) return "";
  const candidates = [
    contact.fk_soc,
    contact.socid,
    contact.company_id,
    contact.companyid,
    contact.rowid,
  ];
  const match = candidates.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );
  return match ? String(match) : "";
};

const normalizeThirdPartyNode = (node) => {
  if (!node) return null;
  const id = deriveThirdPartyId(node);
  const socId = resolveThirdPartySocId(node);
  const children =
    Array.isArray(node.children) && node.children.length > 0
      ? node.children.map((child) => normalizeThirdPartyNode(child)).filter(Boolean)
      : [];

  return {
    id,
    socId,
    name: deriveThirdPartyName(node),
    nameAlias: normalizeAlias(node.name_alias ?? node.nameAlias),
    ref: node.ref || "",
    email: node.email || "",
    parent: node.parent ? String(node.parent) : "",
    status: node.status || "",
    notePrivate: node.note_private || "",
    tierLevel: node.array_options?.options_tier_level || "",
    venueId: node.array_options?.options_venue_id || "",
    wallet: node.Wallet || node.wallet || null,
    children,
  };
};

const fetchContactByEmail = async (email) => {
  const response = await fetch(CONTACT_LIST_URL, {
    method: "GET",
    headers: CRM_HEADERS,
  });

  if (!response.ok) {
    throw new Error("errors.contactStatus");
  }

  const result = await response.json();
  if (result.code !== 200 || !Array.isArray(result.data)) {
    throw new Error(result.message || "errors.invalidContactResponse");
  }

  const normalizedTarget = normalizeEmail(email);
  const contact =
    result.data.find((item) => normalizeEmail(item.email) === normalizedTarget) || null;

  if (!contact) {
    throw new Error("errors.noContact");
  }

  return contact;
};

const fetchThirdPartyList = async (email, maxTier = 6) => {
  const normalizedEmail = normalizeEmail(email);
  const url = new URL(THIRD_PARTY_LIST_URL);
  url.searchParams.set("email", normalizedEmail);
  if (maxTier !== undefined && maxTier !== null) {
    url.searchParams.set("max_tier", String(maxTier));
  }
  const apiUrl = url.toString();
  const response = await fetch(apiUrl, {
    method: "GET",
    headers: CRM_HEADERS,
  });

  if (!response.ok) {
    throw new Error("errors.thirdpartyStatus");
  }

  const result = await response.json();
  if (result.code !== 200) {
    throw new Error(result.message || "errors.thirdpartyFetch");
  }

  const thirdparties = Array.isArray(result.data) ? result.data : [];
  if (!thirdparties.length) {
    throw new Error("errors.noThirdPartyData");
  }

  return thirdparties;
};

const TreeNode = styled(Box)(({ theme, level = 0 }) => ({
  display: "flex",
  flexDirection: "column",
  marginLeft: level * 16,
  paddingLeft: 8,
  borderLeft: "1px dashed",
  borderColor: theme.palette.divider,
}));

const TreeNodeHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "4px 0",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const WalletInfo = ({ wallet, t }) => {
  if (!wallet) return null;
  return (
    <Box sx={{ ml: 2, mt: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
      <Typography variant="body2">
        <strong>{t("labels.balance")}</strong> {wallet.lsBalance} {wallet.lsCurrency}
      </Typography>
      <Typography variant="body2">
        <strong>{t("labels.posId")}</strong> {wallet.posPid} ({wallet.posCurrency})
      </Typography>
      <Typography variant="body2">
        <strong>{t("labels.minBet")}</strong> {wallet.posMinBet}
      </Typography>
    </Box>
  );
};

WalletInfo.propTypes = {
  wallet: PropTypes.shape({
    lsBalance: PropTypes.number,
    lsCurrency: PropTypes.string,
    posPid: PropTypes.string,
    posCurrency: PropTypes.string,
    posMinBet: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

const tierLevelKeys = {
  1: "tier.ga",
  2: "tier.la",
  3: "tier.brand",
  4: "tier.store",
  5: "tier.machine",
  6: "tier.machinePair",
};

const updateAliasInTree = (node, targetKey, nextAlias) => {
  if (!node) return node;
  const nodeKey = node.socId || node.id;
  const isMatch = nodeKey && nodeKey === targetKey;
  let childrenChanged = false;
  const nextChildren = Array.isArray(node.children)
    ? node.children.map((child) => {
        const updatedChild = updateAliasInTree(child, targetKey, nextAlias);
        if (updatedChild !== child) {
          childrenChanged = true;
        }
        return updatedChild;
      })
    : node.children;

  if (!isMatch && !childrenChanged) {
    return node;
  }

  return {
    ...node,
    nameAlias: isMatch ? nextAlias : node.nameAlias,
    children: childrenChanged ? nextChildren : node.children,
  };
};

const flattenThirdPartyTree = (node, level = 0, acc = []) => {
  if (!node) return acc;
  acc.push({ node, level });
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => flattenThirdPartyTree(child, level + 1, acc));
  }
  return acc;
};

const ThirdPartyTreeNode = ({ node, level = 0, t }) => {
  const [expanded, setExpanded] = useState(level < 1);
  const primaryLabel = node.name || node.ref || node.id;
  const noteLabel = node.notePrivate ? node.notePrivate : null;
  const tierLabel = node.tierLevel ? t(tierLevelKeys[String(node.tierLevel)]) : null;
  const aliasDisplay = node.nameAlias ? node.nameAlias : "";

  return (
    <TreeNode level={level}>
      <TreeNodeHeader
        onClick={() =>
          setExpanded((prev) => {
            const next = !prev;
            return next;
          })
        }
      >
        <IconButton size="small" sx={{ mr: 1 }}>
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Box>
          <Typography component="span">{primaryLabel}</Typography>
          {noteLabel && (
            <Typography variant="caption" color="textSecondary" display="block">
              {noteLabel}
            </Typography>
          )}
          {aliasDisplay ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Typography variant="body2" color="textSecondary">
                {aliasDisplay}
              </Typography>
            </Box>
          ) : null}
          {tierLabel && (
            <Typography variant="caption" color="textSecondary" display="block">
              {tierLabel}
            </Typography>
          )}
        </Box>
      </TreeNodeHeader>

      <Collapse in={expanded} unmountOnExit>
        {node.wallet && <WalletInfo wallet={node.wallet} t={t} />}
        {node.children?.map((child) => (
          <ThirdPartyTreeNode key={child.id} node={child} level={level + 1} t={t} />
        ))}
      </Collapse>
    </TreeNode>
  );
};

ThirdPartyTreeNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    socId: PropTypes.string,
    name: PropTypes.string,
    nameAlias: PropTypes.string,
    ref: PropTypes.string,
    email: PropTypes.string,
    notePrivate: PropTypes.string,
    tierLevel: PropTypes.string,
    wallet: PropTypes.object,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  level: PropTypes.number,
  t: PropTypes.func.isRequired,
};

ThirdPartyTreeNode.defaultProps = {
  level: 0,
};

const useAgentHierarchy = () => {
  const { t } = useTranslation("agentManagement");
  const { userData } = useUserContext() || {};
  const [hierarchyData, setHierarchyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchThirdPartiesHierarchy = async () => {
    if (!userData?.email) {
      setError(t("errors.emailRequired"));
      setHierarchyData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [contact, thirdparties] = await Promise.all([
        fetchContactByEmail(userData.email),
        fetchThirdPartyList(userData.email),
      ]);

      const normalizedUserEmail = normalizeEmail(userData.email);
      const linkedThirdPartyId = extractLinkedThirdPartyId(contact);

      let matchedRawNode = null;
      if (linkedThirdPartyId) {
        matchedRawNode = findThirdPartyNodeBySoc(thirdparties, linkedThirdPartyId);
      }

      if (!matchedRawNode) {
        matchedRawNode = thirdparties.find(
          (node) => normalizeEmail(node.email) === normalizedUserEmail
        );
      }

      if (!matchedRawNode && thirdparties.length > 0) {
        console.warn(
          "[AgentManagement] Falling back to first thirdparty node; no direct match for contact or email"
        );
        matchedRawNode = thirdparties[0];
      }

      if (!matchedRawNode) {
        throw new Error("errors.noLinkedNode");
      }

      const matchedNode = normalizeThirdPartyNode(matchedRawNode);

      setHierarchyData({
        Username: deriveThirdPartyName(matchedRawNode),
        MatchedNode: matchedNode,
      });
    } catch (err) {
      console.error("Error fetching third parties hierarchy:", err);
      setHierarchyData(null);
      const messageKey = err?.message?.startsWith("errors.") ? err.message : "errors.generic";
      setError(t(messageKey));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.email) {
      fetchThirdPartiesHierarchy();
    }
  }, [userData?.email]);

  return { hierarchyData, setHierarchyData, isLoading, error };
};

export {
  CRM_HEADERS,
  normalizeAlias,
  normalizeEmail,
  updateAliasInTree,
  flattenThirdPartyTree,
  ThirdPartyTreeNode,
  useAgentHierarchy,
};
